import { useQueryClient } from '@tanstack/react-query'
import { useIsLoadingWithPermission, usePermissions } from 'contexts/permissions.tsx'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCustomerCardAccounts } from '../../api/react-query'
import { CARD_ACCOUNTS } from '../../api/react-query/keys.ts'
import type { BackOfficeCustomerDtoV2 } from '../../api/swagger/definitions/backoffice'
import CardPayouts from '../../components/CardPayouts.tsx'
import CardPETab from '../../components/CardPETab.tsx'
import TextContainer from '../../components/TextContainer.tsx'
import { CustomerTransactionsTable } from '../../features/customer-transactions-table'
import { TabSets } from '../../helpers/TabsHelpers.ts'
import FigmaBox from '../../mynt-components/components/FigmaBox.tsx'
import MyntLoader from '../../mynt-components/components/MyntLoader.tsx'
import Tabs from '../../mynt-components/components/Tabs.tsx'
import { TextKeys } from '../../tiger/libs/TextRepository.ts'
import { CustomerFraudCasesTab } from '../FraudCases/components/CustomerFraudCasesTab'
import { Invoices } from '../invoices'
import { CustomerPisDepositsTab } from '../PisDeposits/components/CustomerPisDepositsTab'
import { CustomerTravelTab } from '../Travel/components/CustomerTravelTab'
import CustomerCardTabAutoTopup from './CustomerCardTabAutoTopup.tsx'
import CustomerCardTabChangeStatusModal from './CustomerCardTabChangeStatusModal.tsx'
import CustomerCardTabCreditOnCard from './CustomerCardTabCreditOnCard.tsx'
import CustomerCardTabLowBalanceAlert from './CustomerCardTabLowBalanceAlert.tsx'
import CustomerCardTabOverview from './CustomerCardTabOverview.tsx'

interface CustomerCardTabProps {
  customerId: string
  customer: BackOfficeCustomerDtoV2
  patchCustomerDetails: (data: any) => Promise<any>
}

const CustomerCardTab: React.FC<CustomerCardTabProps> = ({ customerId, customer }) => {
  const queryClient = useQueryClient()

  const { tab1: parentTab, tab2: activeTab = null } = useParams<'tab2'>() as Record<string, string>
  const navigate = useNavigate()

  const permissions = {
    overview: usePermissions('customers.cards.overview.view'),
    enfuce: usePermissions('customers.cards.overview.enfuce'),
    transactions: usePermissions('customers.cards.transactions.view'),
    payouts: usePermissions('customers.cards.payouts'),
    invoices: usePermissions('customers.cards.invoices'),
    pe: usePermissions('customers.cards.pe'),
    autoTopUp: usePermissions('customers.cards.autoTopUp'),
    lowBalanceAlert: usePermissions('customers.cards.lowBalanceAlert'),
    creditOnCard: usePermissions('customers.cards.creditOnCard'),
    fraudCases: usePermissions('fraud-cases'),
    pisDeposits: usePermissions('customers.cards.pis-deposits'),
    travel: usePermissions('customers.cards.travel'),
    billingSettings: usePermissions('customers.cards.billing.view'),
  }

  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false)

  const {
    data: cardAccounts,
    isLoading: _isLoadingCardAccounts,
    isFetching: _isFetchingCardAccounts,
  } = useCustomerCardAccounts(customerId, { retry: false, retryOnMount: false, enabled: permissions.enfuce })
  const currentCardAccount = useMemo(() => (cardAccounts && cardAccounts?.length > 0 ? cardAccounts[0] : undefined), [cardAccounts])

  const isLoadingCardAccounts = useIsLoadingWithPermission(_isLoadingCardAccounts, 'customers.cards.overview.enfuce')
  const isFetchingCardAccounts = useIsLoadingWithPermission(_isFetchingCardAccounts, 'customers.cards.overview.enfuce')

  if (isLoadingCardAccounts || isFetchingCardAccounts) {
    return <MyntLoader fullWidth />
  }

  const handleTabChange = (tabIndex: number) => {
    const tab = tabs[tabIndex]

    if (!tab) return

    navigate(`${parentTab}/${tab.id}`, { replace: true })
  }

  const tabs = [
    {
      id: 'overview',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Overview'} />,
      contentElement: (
        <CustomerCardTabOverview
          customer={customer}
          currentCardAccount={currentCardAccount}
          setIsChangeStatusModalOpen={setIsChangeStatusModalOpen}
        />
      ),
      enable: permissions.overview,
    },
    {
      id: 'transactions',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Transactions'} />,
      contentElement: <CustomerTransactionsTable customerId={customerId} />,
      enable: permissions.transactions,
    },
    {
      id: 'payouts',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Payouts'} />,
      contentElement: <CardPayouts customerId={customerId} />,
      enable: permissions.payouts,
    },
    {
      id: 'invoices',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Invoices'} />,
      contentElement: <Invoices customerId={customerId} page={'CUSTOMER'} />,
      enable: permissions.invoices,
    },
    {
      id: 'pe',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'PE'} />,
      contentElement: <CardPETab customerId={customerId} />,
      enable: permissions.pe,
    },
    {
      id: 'auto-top-up',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={TextKeys.autoTopUpHeading.characters} />,
      contentElement: <CustomerCardTabAutoTopup customerId={customerId} setIsChangeStatusModalOpen={setIsChangeStatusModalOpen} />,
      enable: permissions.autoTopUp,
    },
    {
      id: 'low-balance-alert',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={TextKeys.lowBalanceAlertHeading.characters} />,
      contentElement: <CustomerCardTabLowBalanceAlert customerId={customerId} setIsChangeStatusModalOpen={setIsChangeStatusModalOpen} />,
      enable: permissions.lowBalanceAlert,
    },
    {
      id: 'credit-on-card',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Credit on card'} />,
      contentElement: <CustomerCardTabCreditOnCard customerId={customerId} />,
      enable: permissions.creditOnCard,
    },
    {
      id: 'customer-fraud-cases',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Fraud cases'} />,
      contentElement: <CustomerFraudCasesTab customerId={customerId} />,
      enable: permissions.fraudCases,
    },
    {
      id: 'pis-deposits',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Direct deposits'} />,
      contentElement: <CustomerPisDepositsTab customerId={customerId} />,
      enable: permissions.pisDeposits,
    },
    {
      id: 'travel',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Travel'} />,
      contentElement: <CustomerTravelTab customerId={customerId} />,
      enable: permissions.travel,
    },
  ]

  const activeTabIndex = tabs.findIndex((t) => t.id === activeTab)

  return (
    <FigmaBox fullWidth>
      <Tabs
        onTabChange={handleTabChange}
        tab={activeTabIndex >= 0 ? activeTabIndex : null}
        fullPage
        alwaysShowNavHeadings
        tabKeyNameSpace={TabSets.CUSTOMER_CARD}
        tabs={tabs}
      />
      {!!currentCardAccount && (
        <CustomerCardTabChangeStatusModal
          isOpen={isChangeStatusModalOpen}
          setIsModalOpen={setIsChangeStatusModalOpen}
          customerId={customerId}
          cardAccount={currentCardAccount}
          onSuccess={async () =>
            queryClient.invalidateQueries({
              queryKey: [CARD_ACCOUNTS, customerId],
            })
          }
        />
      )}
    </FigmaBox>
  )
}

export default CustomerCardTab
